import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  FormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { FormValidation } from '@core/services/form-validation';

export interface DropdownOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-input-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './input-select.html',
  styleUrls: ['./input-select.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputSelect),
      multi: true,
    },
  ],
})
export class InputSelect implements ControlValueAccessor, OnInit {
  // 🔥 static tracker for open dropdown
  private static openDropdown: InputSelect | null = null;
  private static nextId = 0;

  @Input() label: string = '';
  @Input() placeholder: string = 'Select an option';
  @Input() options: DropdownOption[] = [];
  @Input() isRequired: boolean = false;
  @Input() formControlName: string = '';
  @Input() control: AbstractControl | null = new FormControl();
  @Input() showSearch: boolean = false;
  @Input() title: string = '';
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() disabled: boolean = false;
  @Output() change = new EventEmitter<any>();
  isOpen = false;
  value: string | number | null = null;
  searchTerm = '';
  filteredOptions: DropdownOption[] = [];
  activeIndex: number = -1;
  private pointerDownOnTrigger = false;

  readonly triggerId = `input-select-trigger-${++InputSelect.nextId}`;
  readonly labelId = `input-select-label-${InputSelect.nextId}`;
  readonly listboxId = `input-select-listbox-${InputSelect.nextId}`;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  @ViewChild('trigger', { static: true })
  private readonly triggerRef?: ElementRef<HTMLElement>;

  constructor(
    public formValidation: FormValidation,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.filteredOptions = [...this.options];
  }

  ngOnChanges(): void {
    // keep filtered list in sync when options change
    if (!this.isOpen) {
      this.filteredOptions = [...this.options];
      return;
    }
    this.filterOptions();
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    if (this.control?.disabled || this.disabled) return;

    if (this.isOpen) {
      // already open → close it
      this.close();
    } else {
      // close previously open dropdown if exists
      if (InputSelect.openDropdown && InputSelect.openDropdown !== this) {
        InputSelect.openDropdown.close();
      }
      InputSelect.openDropdown = this;
      this.isOpen = true;
      this.searchTerm = '';
      this.filteredOptions = [...this.options];
      this.setActiveIndexFromValue();
    }

    if (this.isOpen) this.onTouched();
  }

  selectOption(option: DropdownOption, event: Event) {
    event?.stopPropagation(); // ✅ prevent bubbling up
    if (option.disabled) return;
    this.value = option.value;
    this.onChange(this.value);
    this.close();
    this.onTouched();

    this.control?.markAsTouched();
    this.control?.markAsDirty();
    this.control?.updateValueAndValidity();
    this.change.emit(option);
  }

  filterOptions() {
    const term = this.searchTerm.toLowerCase();
    this.filteredOptions = this.options.filter(opt =>
      opt.label.toLowerCase().includes(term)
    );
    if (this.activeIndex >= this.filteredOptions.length) this.activeIndex = -1;
  }

  writeValue(value: any): void {
    this.value = value;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  get selectedLabel(): string {
    return this.options.find(opt => opt.value === this.value)?.label || '';
  }

  focusTrigger(event?: Event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.triggerRef?.nativeElement?.focus?.();
  }

  onTriggerPointerDown() {
    this.pointerDownOnTrigger = true;
  }

  onTriggerFocus() {
    if (this.control?.disabled || this.disabled) return;
    // If focus came from mouse/touch click, don't auto-open (click handler will manage it)
    if (this.pointerDownOnTrigger) {
      this.pointerDownOnTrigger = false;
      return;
    }
    this.open();
  }

  onTriggerKeydown(event: KeyboardEvent) {
    if (this.control?.disabled || this.disabled) return;

    switch (event.key) {
      case 'Enter':
      case ' ': {
        event.preventDefault();
        if (this.isOpen) {
          const opt = this.activeOption;
          if (opt && !opt.disabled) {
            this.selectOption(opt, event);
            this.focusTrigger();
            return;
          }
        }
        this.toggleDropdown(event);
        return;
      }
      case 'Escape': {
        if (!this.isOpen) return;
        event.preventDefault();
        this.close();
        return;
      }
      case 'ArrowDown': {
        event.preventDefault();
        if (!this.isOpen) this.open();
        this.moveActive(1);
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        if (!this.isOpen) this.open();
        this.moveActive(-1);
        return;
      }
      default:
        return;
    }
  }

  get activeOption(): DropdownOption | null {
    const opt = this.filteredOptions[this.activeIndex];
    return opt ?? null;
  }

  get activeDescendantId(): string | null {
    if (!this.isOpen) return null;
    if (this.activeIndex < 0) return null;
    return `opt-${this.listboxId}-${this.activeIndex}`;
  }

  get errorMessage(): string | null {
    if (this.control?.touched && this.control?.invalid) {
      return this.formValidation.getValidationMessage(this.control, this.formControlName);
    }
    return null;
  }
  get required(): boolean {
    if (!this.control || !this.control.validator) return false;
    const validator = this.control.validator({} as AbstractControl);
    return !!validator?.['required'] || this.isRequired;
  }

  private close() {
    this.isOpen = false;
    this.activeIndex = -1;
    if (InputSelect.openDropdown === this) {
      InputSelect.openDropdown = null;
    }
  }

  private open() {
    if (this.isOpen) return;
    if (InputSelect.openDropdown && InputSelect.openDropdown !== this) {
      InputSelect.openDropdown.close();
    }
    InputSelect.openDropdown = this;
    this.isOpen = true;
    this.searchTerm = '';
    this.filteredOptions = [...this.options];
    this.setActiveIndexFromValue();
    this.onTouched();
  }

  private setActiveIndexFromValue() {
    const idx = this.filteredOptions.findIndex(opt => opt.value === this.value && !opt.disabled);
    this.activeIndex = idx >= 0 ? idx : this.firstEnabledIndex();
    this.scrollActiveIntoView();
  }

  private firstEnabledIndex(): number {
    return this.filteredOptions.findIndex(opt => !opt.disabled);
  }

  private moveActive(delta: 1 | -1) {
    if (!this.filteredOptions.length) return;

    let i = this.activeIndex;
    const max = this.filteredOptions.length;

    for (let step = 0; step < max; step++) {
      i = (i + delta + max) % max;
      if (!this.filteredOptions[i]?.disabled) {
        this.activeIndex = i;
        this.scrollActiveIntoView();
        return;
      }
    }
  }

  private scrollActiveIntoView() {
    if (!this.isOpen) return;
    if (this.activeIndex < 0) return;

    // Wait for DOM to render `@if(isOpen)` + `@for(...)` updates
    setTimeout(() => {
      const root = this.elementRef.nativeElement;
      const el = root.querySelector<HTMLElement>(`#opt-${this.listboxId}-${this.activeIndex}`);
      el?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }

  @HostListener('document:click', ['$event'])
  closeDropdownOnOutsideClick(event: MouseEvent) {
    if (!this.isOpen) return;

    const target = event.target as Node | null;
    if (target && this.elementRef.nativeElement.contains(target)) return;

    this.close();
  }

  @HostListener('document:keydown.escape')
  closeOnEscape() {
    if (!this.isOpen) return;
    this.close();
  }

  @HostListener('document:focusin', ['$event'])
  closeOnFocusLeave(event: FocusEvent) {
    if (!this.isOpen) return;
    const target = event.target as Node | null;
    if (target && this.elementRef.nativeElement.contains(target)) return;
    this.close();
  }

  onOptionMouseEnter(index: number) {
    if (this.filteredOptions[index]?.disabled) return;
    this.activeIndex = index;
    this.scrollActiveIntoView();
  }

  onOptionKeydown(event: KeyboardEvent) {
    if (!this.isOpen) return;

    switch (event.key) {
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const opt = this.filteredOptions[this.activeIndex];
        if (!opt || opt.disabled) return;
        // synthetic event is fine; handler only stops propagation
        this.selectOption(opt, event);
        this.focusTrigger();
        return;
      }
      case 'Escape': {
        event.preventDefault();
        this.close();
        this.focusTrigger();
        return;
      }
      case 'ArrowDown': {
        event.preventDefault();
        this.moveActive(1);
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        this.moveActive(-1);
        return;
      }
      case 'Tab': {
        this.close();
        return;
      }
      default:
        return;
    }
  }
}
